import { signal } from '@angular/core';

/**
 * A per-organization cached value: fetched at most once per organization, shared between concurrent
 * callers, and invalidated on an organization switch.
 *
 * The correctness this centralizes is the stale response. An organization switch mid-request must
 * start its own load rather than adopt the previous organization's result, and a response that lands
 * after a switch must be dropped rather than published under the new organization.
 */
export class OrganizationScopedResource<T> {
  public readonly value = signal<T | undefined>(undefined);

  private loadedForOrganizationId: number | undefined = undefined;
  private loadingPromise: Promise<T | undefined> | undefined = undefined;
  private loadingOrganizationId: number | undefined = undefined;

  /**
   * @param label Names this resource in error logs.
   * @param activeOrganizationId Reads the organization the session is currently on.
   * @param fetcher Loads the value; returns undefined when the load fails.
   * @param onSettled Called only when the response is still current, so a consumer tracking load
   * state does not record the previous organization's outcome against the new one.
   */
  constructor(
    private readonly label: string,
    private readonly activeOrganizationId: () => number | undefined,
    private readonly fetcher: (organizationId: number) => Promise<T | undefined>,
    private readonly onSettled?: (loaded: boolean) => void
  ) {}

  public async load(): Promise<T | undefined> {
    const organizationId = this.activeOrganizationId();

    if (organizationId === undefined) {
      return undefined;
    }

    if (this.loadedForOrganizationId === organizationId) {
      return this.value();
    }

    if (this.loadingPromise && this.loadingOrganizationId === organizationId) {
      return this.loadingPromise;
    }

    const loadingPromise = this.fetchFor(organizationId);
    this.loadingPromise = loadingPromise;
    this.loadingOrganizationId = organizationId;

    try {
      return await loadingPromise;
    } finally {
      // Only clear what is still ours - an org switch during the request replaces these with the
      // newer organization's fetch, which has to stay reusable.
      if (this.loadingPromise === loadingPromise) {
        this.loadingPromise = undefined;
        this.loadingOrganizationId = undefined;
      }
    }
  }

  /** Drops the cached value so the next load() refetches. */
  public invalidate(): void {
    this.value.set(undefined);
    this.loadedForOrganizationId = undefined;
  }

  /** Publishes a value the caller already holds, so a known-fresh response avoids a round trip. */
  public prime(organizationId: number, value: T): void {
    this.value.set(value);
    this.loadedForOrganizationId = organizationId;
  }

  private async fetchFor(organizationId: number): Promise<T | undefined> {
    let result: T | undefined;

    try {
      result = await this.fetcher(organizationId);
    } catch (error) {
      console.error(`Error fetching ${this.label}:`, error);
      result = undefined;
    }

    if (this.activeOrganizationId() !== organizationId) {
      return undefined;
    }

    if (result !== undefined) {
      this.value.set(result);
      this.loadedForOrganizationId = organizationId;
    }

    this.onSettled?.(result !== undefined);

    return result;
  }
}
